import { api, el, toast } from "../../ui/dom";
import { openModal } from "../../ui/modal";
import { card, ib, table } from "../../ui/record";
import { icon } from "../../ui/icons";
import {
  BILL_BY_HINT,
  CLAIM_LINES_HINT,
  type BillBy,
  type ClaimLines,
} from "../../../server/rules/billing-rules";
import { field, refuse, select } from "../clients/forms";

// The Home Care client's Billing card (brief 1E-1, mockup hClientRec's Billing): the format and
// the terms from its contract, its current rates, and the margin to date with the Money switch.
// Rates are added in Home Care › Bill rates; the terms on the contract. Brief 1E-4: how a short or
// denied line is rebilled for this client (what's still open, or the whole line as a corrected
// claim), Billing at Edit to change. Brief 1E-5 (mockup v58): Mileage charged: not billed, or a rate
// per mile from a date; Bill the week adds one mileage line per visit with miles while it's billed.
// Brief 1E-13 (mockup v63): Hours billed (hours worked, scheduled hours with the short-visit
// minutes, or a set number of hours per visit), last week's visits with Scheduled, Worked, Billed
// and Paid, and the pencil to change a visit's billed hours with why, until the invoice is approved.

interface Answer {
  contract: { id: string; number: string | null } | null;
  terms: string;
  format: string;
  billsBy: { how: string; choices: { value: string; label: string }[]; canEdit: boolean };
  rates: { service: string; rate: string }[];
  margin: { billed: string; pay: string; margin: string; pct: number | null } | null;
  rebill: { how: string; choices: { value: string; label: string }[]; canEdit: boolean };
  mileage: { words: string; today: string; canEdit: boolean };
  hoursBilled: HoursBilled;
}

interface HoursBilled {
  client: string;
  how: "worked" | "scheduled" | "set";
  shortMinutes: number;
  setHours: number | null;
  choices: { value: string; label: string }[];
  week: string;
  visits: {
    visitId: string;
    visit: string;
    patient: string | null;
    scheduled: string;
    worked: string;
    billed: string;
    changed: string | null;
    paid: string;
    locked: string | null;
  }[];
  canEdit: boolean;
}

export async function clientBillingCard(
  host: HTMLElement,
  clientId: string,
  onBillsBy?: () => void,
) {
  const res = await api<Answer>("GET", `/api/clients/${encodeURIComponent(clientId)}/billing`);
  if (!res.ok) return;
  const a = res.body;
  const rows: [string, string][] = [
    ["Payment terms", a.terms],
    ["Contract", a.contract ? `No. ${a.contract.number ?? "–"}` : "No contract today"],
  ];
  if (a.margin)
    rows.push(
      ["Billed to date", a.margin.billed],
      ["Caregiver pay", a.margin.pay],
      ["Margin", `${a.margin.margin}${a.margin.pct !== null ? ` · ${a.margin.pct}%` : ""}`],
    );
  const rebill = el(
    "select",
    { "aria-label": "Rebill", disabled: !a.rebill.canEdit },
    ...a.rebill.choices.map((c) =>
      el("option", { value: c.value, selected: c.value === a.rebill.how }, c.label),
    ),
  );
  rebill.addEventListener("change", async () => {
    const r = await api("PUT", `/api/clients/${encodeURIComponent(clientId)}/rebill`, {
      how: rebill.value,
    });
    if (!r.ok) {
      rebill.value = a.rebill.how;
      return toast("That wasn't saved", (r.body as { error?: string }).error);
    }
    a.rebill.how = rebill.value;
    toast("Saved", rebill.selectedOptions[0]?.textContent ?? "");
  });
  // Brief 1E-15: Bills by, from the client's type; changing it redraws the record's claim card.
  const billsBy = el(
    "select",
    { "aria-label": "Bills by", disabled: !a.billsBy.canEdit },
    ...a.billsBy.choices.map((c) =>
      el("option", { value: c.value, selected: c.value === a.billsBy.how }, c.label),
    ),
  );
  billsBy.addEventListener("change", async () => {
    const r = await api("PUT", `/api/clients/${encodeURIComponent(clientId)}/bills-by`, {
      billsBy: billsBy.value,
    });
    if (!r.ok) {
      billsBy.value = a.billsBy.how;
      return toast("That wasn't saved", (r.body as { error?: string }).error);
    }
    toast("Saved", `Bills by ${billsBy.selectedOptions[0]?.textContent ?? ""}`);
    onBillsBy?.();
  });
  const mileage = el(
    "span",
    {},
    el("span", {}, a.mileage.words),
    a.mileage.canEdit
      ? ib("pencil", "Change what this client is charged a mile, from a date", () =>
          mileageModal(
            clientId,
            a.mileage.today,
            () => void clientBillingCard(host, clientId, onBillsBy),
          ),
        )
      : "",
  );
  const hours = el("div", {});
  hoursBilledField(hours, clientId, a.hoursBilled);
  host.replaceChildren(
    card(
      "Billing",
      "Bill rates are on the contract's bill rate card in Home Care › Bill rates; terms on the contract.",
      [],
      el(
        "dl",
        { class: "kv" },
        el("dt", {}, "Bills by"),
        el("dd", {}, billsBy),
        ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]),
        el("dt", {}, "Rebill"),
        el("dd", {}, rebill),
        el("dt", {}, "Bill rate a mile"),
        el("dd", {}, mileage),
      ),
      hours,
      a.rates.length
        ? el(
            "div",
            { style: "margin-top:10px" },
            table(
              [
                { h: "Service", f: (r) => r.service },
                { h: "Bill rate", num: true, f: (r) => el("b", {}, r.rate) },
              ],
              a.rates,
              undefined,
              "Bill rates",
            ),
          )
        : el("p", { class: "muted small" }, "No bill rates yet."),
    ),
  );
}

/** What the client is charged for mileage from a date: a rate a mile, or not billed. */
function mileageModal(clientId: string, today: string, done: () => void) {
  const how = select(
    "Mileage",
    [
      ["rate", "A bill rate a mile"],
      ["none", "Not billed"],
    ],
    "rate",
  );
  const rate = field("Bill rate a mile ($)", "1.00", { inputmode: "decimal" });
  const from = field("From", today, { type: "date" });
  how.input.addEventListener("change", () => {
    rate.el.hidden = how.input.value === "none";
  });
  const go = el("button", { type: "button", class: "btn pri" }, "Save");
  const m = openModal({
    title: "Bill rate a mile",
    sub: "Miles a caregiver drives for this client (errands, appointments) go on the invoice as their own line at this bill rate. It saves as a new version of the bill rate card.",
    body: [el("div", { class: "form", style: "max-width:none" }, how.el, rate.el, from.el)],
    actions: [go],
  });
  go.addEventListener("click", async () => {
    go.disabled = true;
    const r = await api("PUT", `/api/clients/${encodeURIComponent(clientId)}/mileage`, {
      ...(how.input.value === "none" ? { billed: false } : { rate: rate.input.value }),
      from: from.input.value,
    });
    go.disabled = false;
    if (!r.ok) return refuse(m.note, (r.body as { error?: string }).error);
    m.close();
    toast("Bill rate a mile saved", (r.body as { words?: string }).words);
    done();
  });
}

/**
 * Hours billed (mockup v63): the three choices; the minutes with Scheduled hours; the hours a visit
 * with a set number; last week's visits with Scheduled, Worked, Billed and Paid, each with the
 * pencil to change its billed hours. Saved as it's changed (Billing at Edit).
 */
function hoursBilledField(host: HTMLElement, clientId: string, h: HoursBilled) {
  const put = async (data: Record<string, unknown>) => {
    const r = await api<HoursBilled>(
      "PUT",
      `/api/clients/${encodeURIComponent(clientId)}/hours-billed`,
      data,
    );
    if (!r.ok) {
      toast("That wasn't saved", (r.body as { error?: string }).error);
      return hoursBilledField(host, clientId, h);
    }
    toast("Saved", r.body.choices.find((c) => c.value === r.body.how)?.label ?? "");
    hoursBilledField(host, clientId, r.body);
  };
  const how = el(
    "select",
    { id: "hb-how", disabled: !h.canEdit },
    ...h.choices.map((c) => el("option", { value: c.value, selected: c.value === h.how }, c.label)),
  );
  how.addEventListener("change", () => void put({ how: how.value }));
  const row = (...kids: (Node | string)[]) =>
    el(
      "div",
      {
        class: "row",
        style: "display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:6px",
      },
      ...kids,
    );
  let extra: Node | null = null;
  if (h.how === "set") {
    const n = el("input", {
      type: "number",
      min: "0.25",
      step: "0.25",
      value: String(h.setHours ?? ""),
      style: "max-width:90px",
      "aria-label": "Hours per visit",
      disabled: !h.canEdit,
    });
    n.addEventListener("change", () => void put({ how: "set", setHours: n.value }));
    extra = row(
      n,
      el(
        "span",
        { class: "small" },
        "hours a visit, however long it ran. Continuous care (24/7) bills each covered shift.",
      ),
    );
  } else if (h.how === "scheduled") {
    const m = el("input", {
      type: "number",
      min: "0",
      max: "120",
      value: String(h.shortMinutes),
      style: "max-width:80px",
      "aria-label": "Minutes short",
      disabled: !h.canEdit,
    });
    m.addEventListener("change", () => void put({ how: "scheduled", shortMinutes: m.value }));
    extra = row(
      el("span", { class: "small" }, "When a visit is short by more than"),
      m,
      el("span", { class: "small" }, "minutes, bill the hours worked."),
    );
  }
  const visits = h.visits.length
    ? el(
        "div",
        { style: "margin-top:8px" },
        table(
          [
            {
              h: "Visit",
              f: (r) =>
                el(
                  "span",
                  {},
                  el("b", {}, r.visit),
                  r.patient ? el("div", { class: "small muted" }, r.patient) : "",
                ),
            },
            { h: "Scheduled", num: true, f: (r) => r.scheduled },
            { h: "Worked", num: true, f: (r) => r.worked },
            {
              h: "Billed",
              num: true,
              f: (r) =>
                el(
                  "span",
                  {},
                  el("b", {}, r.billed),
                  r.changed ? el("div", { class: "small muted" }, r.changed) : "",
                ),
            },
            { h: "Paid", num: true, f: (r) => r.paid },
            {
              h: "",
              f: (r) =>
                h.canEdit
                  ? ib("pencil", "Change billed hours", () =>
                      r.locked
                        ? toast(r.locked)
                        : changeHoursModal(h, r, (next) => hoursBilledField(host, clientId, next)),
                    )
                  : "",
            },
          ],
          h.visits,
          undefined,
          `Visits ${h.week}`,
        ),
      )
    : null;
  host.replaceChildren(
    el(
      "div",
      { class: "field", style: "margin-top:12px" },
      el("label", { for: "hb-how" }, "Hours billed"),
      how,
      extra,
      visits,
      el("span", { class: "hint" }, "Pay always follows the clock."),
    ),
  );
}

/** Change billed hours (mockup v63): Billed hours and Why; the line then shows "Changed · why". */
function changeHoursModal(
  h: HoursBilled,
  r: HoursBilled["visits"][number],
  done: (next: HoursBilled) => void,
) {
  const hours = field("Billed hours", r.billed === "–" ? "" : r.billed, {
    type: "number",
    min: "0",
    step: "0.25",
  });
  const why = field("Why", "", { placeholder: "e.g. Client bought 6 hours a visit" });
  const save = el("button", { type: "submit", class: "btn pri" }, "Save");
  const cancel = el("button", { type: "button", class: "btn" }, "Cancel");
  const form = el(
    "form",
    { class: "form", style: "max-width:none" },
    hours.el,
    why.el,
    el(
      "p",
      { class: "small muted", style: "margin:0" },
      "Paid hours stay as clocked. Billed hours can change until the invoice is approved; after that it's a correction.",
    ),
    el("div", { class: "actions" }, save, cancel),
  );
  const m = openModal({
    title: "Change billed hours",
    sub: `${r.visit} · ${r.patient ?? h.client}`,
    body: [form],
  });
  cancel.addEventListener("click", () => m.close());
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!why.input.value.trim()) return refuse(m.note, "Say why the billed hours changed.");
    save.disabled = true;
    const res = await api<HoursBilled>(
      "POST",
      `/api/billing/visits/${encodeURIComponent(r.visitId)}/hours`,
      { hours: hours.input.value, why: why.input.value },
    );
    save.disabled = false;
    if (!res.ok) return refuse(m.note, (res.body as { error?: string }).error);
    m.close();
    toast("Billed hours changed", "In the history");
    done(res.body);
  });
}

// ---- Billing rules (brief 1E-35, mockup v93) -----------------------------------------------------------

interface Rules {
  contract: { id: string; number: string | null } | null;
  summary: string;
  rules: {
    billBy: string;
    unit: string;
    claimLines: string;
    invoicing: string;
    periodLabel: string;
  };
  hints: { billBy: string; claimLines: string };
  vendorNumber: string;
  packet: { notes: boolean; timesheets: boolean; carePlan: boolean };
  claim: string;
  choices: Record<
    "billBy" | "unit" | "claimLines" | "invoicing" | "periodLabel",
    { value: string; label: string }[]
  >;
  canEdit: boolean;
}

/**
 * The client's Billing rules card: Bill by, Unit, Claim lines, Invoicing, Period on the invoice,
 * Vendor number and the Billing packet, saved together (Billing at Edit). An authorization can set
 * its own Bill by.
 */
export async function billingRulesCard(host: HTMLElement, clientId: string) {
  const res = await api<Rules>("GET", `/api/clients/${encodeURIComponent(clientId)}/billing-rules`);
  if (!res.ok) return;
  draw(res.body);

  function draw(r: Rules) {
    const pick = (label: string, key: keyof Rules["choices"]) => {
      const s = select(
        label,
        r.choices[key].map((c) => [c.value, c.label] as const),
        r.rules[key],
      );
      s.input.disabled = !r.canEdit;
      return s;
    };
    const billBy = pick("Bill by", "billBy");
    const billHint = el("span", { class: "hint" }, r.hints.billBy);
    billBy.el.append(billHint);
    const unit = pick("Unit", "unit");
    const lines = pick("Claim lines", "claimLines");
    const linesHint = el("span", { class: "hint" }, r.hints.claimLines);
    lines.el.append(linesHint);
    const inv = pick("Invoicing", "invoicing");
    const label = pick("Period on the invoice", "periodLabel");
    const vendor = field("Vendor number", r.vendorNumber, {
      placeholder: "The client's number for this company",
      maxlength: "40",
    });
    vendor.input.disabled = !r.canEdit;
    vendor.el.append(el("span", { class: "hint" }, "Printed under Bill to."));
    billBy.input.addEventListener("change", () => {
      billHint.textContent = BILL_BY_HINT[billBy.input.value as BillBy] ?? "";
    });
    lines.input.addEventListener("change", () => {
      linesHint.textContent = CLAIM_LINES_HINT[lines.input.value as ClaimLines] ?? "";
    });
    const tick = (text: string, on: boolean, disabled = !r.canEdit) => {
      const i = el("input", { type: "checkbox", checked: on, disabled });
      return { i, el: el("label", { class: "ptask" }, i, ` ${text}`) };
    };
    const claim = tick(r.claim, true, true);
    const notes = tick("Visit notes for the dates billed", r.packet.notes);
    const sheets = tick("Timesheets", r.packet.timesheets);
    const plan = tick("Current care plan", r.packet.carePlan);
    const save = el("button", { type: "button", class: "btn sm pri" }, icon("check"), " Save");
    save.hidden = !r.canEdit;
    save.addEventListener("click", async () => {
      save.disabled = true;
      const s = await api<Rules>(
        "PUT",
        `/api/clients/${encodeURIComponent(clientId)}/billing-rules`,
        {
          billBy: billBy.input.value,
          unit: unit.input.value,
          claimLines: lines.input.value,
          invoicing: inv.input.value,
          periodLabel: label.input.value,
          vendorNumber: vendor.input.value,
          packet: {
            notes: notes.i.checked,
            timesheets: sheets.i.checked,
            carePlan: plan.i.checked,
          },
        },
      );
      save.disabled = false;
      if (!s.ok) return toast("That wasn't saved", (s.body as { error?: string }).error);
      toast("Billing rules saved", "New invoices follow them");
      draw(s.body);
    });
    const c = card(
      el("span", {}, icon("law"), " Billing rules"),
      r.contract ? r.summary : "Add a contract first: the billing rules are on the contract.",
      [save],
      el(
        "div",
        { class: "form", style: "max-width:none" },
        el(
          "div",
          { class: "grid2", style: "gap:12px" },
          billBy.el,
          unit.el,
          lines.el,
          inv.el,
          label.el,
          vendor.el,
        ),
        el(
          "fieldset",
          { class: "field" },
          el("legend", {}, "Billing packet"),
          claim.el,
          notes.el,
          sheets.el,
          plan.el,
        ),
        el(
          "p",
          { class: "small muted", style: "margin:0" },
          "An authorization can set its own Bill by.",
        ),
      ),
    );
    c.setAttribute("aria-label", "Billing rules");
    host.replaceChildren(c);
  }
}
